'use client';

import { useEffect, useRef, useCallback } from 'react';

function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { r: 255, g: 255, b: 255 };

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

type Props = {
  colors?: string[];
  speed?: number;
  intensity?: number;
};

export default function LightWavesBackground({
  colors = ['#3b82f6', '#60a5fa', '#93c5fd'],
  speed = 0.6,
  intensity = 0.7,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wavesRef = useRef<any[]>([]);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  const initWaves = useCallback(
    (height: number) => {
      const waves = [];
      const waveCount = 5;

      for (let i = 0; i < waveCount; i++) {
        waves.push({
          y: height * (0.3 + (i / waveCount) * 0.5),
          amplitude: height * (0.12 + Math.random() * 0.1),
          frequency: 0.002 + Math.random() * 0.002,
          speed: (0.2 + Math.random() * 0.3) * (i % 2 === 0 ? 1 : -1),
          phase: Math.random() * Math.PI * 2,
          color: colors[i % colors.length],
          opacity: 0.25 + Math.random() * 0.15,
        });
      }

      wavesRef.current = waves;
    },
    [colors]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;

    const updateSize = () => {
      const rect = container.getBoundingClientRect();
      width = rect.width;
      height = rect.height;

      canvas.width = width;
      canvas.height = height;

      initWaves(height);
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(container);

    const draw = () => {
      const time = (Date.now() - startTimeRef.current) * 0.001 * speed;

      ctx.clearRect(0, 0, width, height);

      const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
      bgGradient.addColorStop(0, '#eff6ff');
      bgGradient.addColorStop(0.5, '#dbeafe');
      bgGradient.addColorStop(1, '#bfdbfe');

      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);

      ctx.globalCompositeOperation = 'lighter';

      const glowSpots = [
        { x: width * 0.2, y: height * 0.3, radius: width * 0.4, color: colors[0] },
        { x: width * 0.8, y: height * 0.6, radius: width * 0.35, color: colors[1] },
        { x: width * 0.5, y: height * 0.8, radius: width * 0.3, color: colors[2] },
      ];

      for (const spot of glowSpots) {
        const rgb = hexToRgb(spot.color);

        const gradient = ctx.createRadialGradient(
          spot.x + Math.sin(time * 0.3) * 40,
          spot.y + Math.cos(time * 0.2) * 30,
          0,
          spot.x,
          spot.y,
          spot.radius
        );

        gradient.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},${0.18 * intensity})`);
        gradient.addColorStop(0.5, `rgba(${rgb.r},${rgb.g},${rgb.b},${0.06 * intensity})`);
        gradient.addColorStop(1, 'transparent');

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
      }

      for (const wave of wavesRef.current) {
        const rgb = hexToRgb(wave.color);

        ctx.beginPath();

        for (let x = 0; x <= width; x += 5) {
          const y =
            wave.y +
            Math.sin(x * wave.frequency + time * wave.speed + wave.phase) * wave.amplitude +
            Math.sin(x * wave.frequency * 0.5 + time * wave.speed * 0.7) *
              wave.amplitude *
              0.5;

          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }

        ctx.lineTo(width, height);
        ctx.lineTo(0, height);
        ctx.closePath();

        const gradient = ctx.createLinearGradient(0, wave.y - wave.amplitude, 0, height);

        gradient.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},${wave.opacity * intensity})`);
        gradient.addColorStop(0.3, `rgba(${rgb.r},${rgb.g},${rgb.b},${wave.opacity * 0.5 * intensity})`);
        gradient.addColorStop(1, 'transparent');

        ctx.fillStyle = gradient;
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    animationRef.current = requestAnimationFrame(draw);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      resizeObserver.disconnect();
    };
  }, [colors, speed, intensity, initWaves]);

  return (
    <div ref={containerRef} className="absolute inset-0 z-0 overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
    </div>
  );
}